import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { WithdrawalAmountScreen } from '../features/withdrawal/ui/withdrawal-amount-screen';
import {
  getEmployeeDashboard,
  getEmployeeProfile,
  withdrawEmployee,
} from '../shared/api/employee.service';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';
import {
  formatCurrency,
  formatTaxIdInput,
  resolveCurrencyPrefix,
  resolveEmployeeSessionCountryCode,
  resolveTaxIdFieldConfig,
  resolveThousandsSeparator,
} from '../shared/model';

function resolveEmployeeDashboardTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

function resolveWithdrawalBalanceAmount(balance?: string): number {
  const numericBalance = Number.parseFloat(balance ?? '');

  if (!Number.isFinite(numericBalance) || numericBalance < 0) {
    return 0;
  }

  return Math.round(numericBalance);
}

function createWithdrawalIdempotencyKey() {
  return crypto.randomUUID();
}

export function WithdrawalAmountPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authContact, status } = useEmployeeSession();
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const dashboardQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: () => getEmployeeDashboard(resolveEmployeeDashboardTimeZone()),
    queryKey: ['employee', 'dashboard'],
  });
  const profile = profileQuery.data ?? null;
  const dashboardProfile = dashboardQuery.data?.profile ?? null;
  const withdrawalProfile = profile ?? dashboardProfile;
  const countryCode = resolveEmployeeSessionCountryCode(
    withdrawalProfile,
    authContact,
  );
  const availableBalanceAmount = resolveWithdrawalBalanceAmount(
    dashboardQuery.data?.wallet.balance,
  );
  const destinationTaxId = formatTaxIdInput(
    withdrawalProfile?.tin ?? '',
    countryCode,
  );
  const taxIdField = resolveTaxIdFieldConfig(countryCode);
  const withdrawalMutation = useMutation({
    mutationFn: (amount: number) =>
      withdrawEmployee({
        amount,
        idempotencyKey: createWithdrawalIdempotencyKey(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['employee'],
      });
    },
  });

  return (
    <WithdrawalAmountScreen
      avatarUrl={withdrawalProfile?.avatarUrl ?? null}
      availableBalance={formatCurrency(availableBalanceAmount, countryCode)}
      availableBalanceAmount={availableBalanceAmount}
      currencyPrefix={resolveCurrencyPrefix(countryCode)}
      destinationTaxId={destinationTaxId}
      taxIdLabelKey={taxIdField.labelKey}
      onBack={() => {
        if (window.history.length > 1) {
          navigate(-1);
          return;
        }

        navigate('/withdrawal');
      }}
      onOpenHome={() => {
        navigate('/home');
      }}
      onOpenMyPage={() => {
        navigate('/my-page');
      }}
      onOpenSupport={() => undefined}
      onSubmitTransfer={amount => withdrawalMutation.mutateAsync(amount)}
      thousandsSeparator={resolveThousandsSeparator(countryCode)}
    />
  );
}
